import { useMemo } from "react";
import type { FiatPrice } from "@b2bportal/core-types";
import { Product, type Prices } from "@b2bportal/purchase-api";
import {
  useCheckoutState,
  useCheckoutStateSelector,
  getParentState,
} from "@checkout/index";
import { ParentState } from "@hopper-b2b/checkout";
import type { RewardsPrice } from "@hopper-b2b/types";
import {
  getRewardsAmountToApply,
  getRewardsAmountToApplyCashEquivalent,
} from "@/modules/checkout";
import {
  getTripTotalFromQuoteBreakdownWithProducts as getDefaultTripTotalFromQuoteBreakdownWithProducts,
  getCardPaymentAmountFromFulfillment as getDefaultCardPaymentAmountFromFulfillment,
} from "@/modules/flight-checkout/selectors";
import {
  getTripTotalFromQuoteBreakdownWithProducts as getHotelTripTotalFromQuoteBreakdownWithProducts,
  getCardPaymentAmountFromFulfillment as getHotelCardPaymentAmountFromFulfillment,
} from "@/modules/hotel-checkout/selectors";
import { PackageSelectors } from "@/modules/package-checkout/selectors";
import { getRemainingBalanceAfterRewardsApplied } from "@/utilities/rewards";

const getTripTotalFromQuoteBreakdownWithProducts = (
  product?: Product
): ((state: unknown) => Prices | undefined) => {
  switch (product) {
    case Product.Hotel:
    case Product.Homes:
      return getHotelTripTotalFromQuoteBreakdownWithProducts;

    case Product.Package:
      // For packages, use payNowTotal from the quote breakdown (preferred) or
      // /complete (fallback before quote runs) as the base for flex-points
      // calculation. The cart-quote balance only reflects the taxes/fees portion,
      // so applying flex points against it causes incorrect over-redemption.
      return PackageSelectors.getPackagePayNowTotal as (
        state: unknown
      ) => Prices | undefined;

    default:
      return getDefaultTripTotalFromQuoteBreakdownWithProducts;
  }
};

const getCardPaymentAmountFromFulfillment = (
  product?: Product
): ((state: unknown) => FiatPrice | undefined) => {
  switch (product) {
    case Product.Hotel:
    case Product.Homes:
      return getHotelCardPaymentAmountFromFulfillment;

    default:
      return getDefaultCardPaymentAmountFromFulfillment;
  }
};

export interface UseCheckoutRemainingBalanceReturn {
  /** The fiat amount charged to the credit card once rewards and credits are applied. */
  remainingBalance: FiatPrice | undefined;
  /** Trip total used as the base for the remaining-balance calculation. */
  tripTotal: Prices | undefined;
  isConfirmationPage: boolean;
  rewardsAmountToApply: RewardsPrice | undefined;
  rewardsAmountToApplyCashEquivalent: FiatPrice | undefined;
}

/**
 * Single source of truth for the credit-card "Amount due" during checkout and
 * the card payment shown on confirmation. Both the price breakdown
 * (`useBalanceWithPoints`) and the Avion points earn (`useAvionPointsSummary`)
 * consume this so the earn base is always exactly what the card is charged.
 */
export const useCheckoutRemainingBalance = (
  product?: Product,
  finalTripTotal?: Prices
): UseCheckoutRemainingBalanceReturn => {
  const [state] = useCheckoutState();

  const quoteBreakdownTripTotal = useCheckoutStateSelector(
    getTripTotalFromQuoteBreakdownWithProducts(product)
  );

  const rewardsAmountToApply = useCheckoutStateSelector(
    getRewardsAmountToApply
  );

  const rewardsAmountToApplyCashEquivalent = useCheckoutStateSelector(
    getRewardsAmountToApplyCashEquivalent
  );

  const parentState = getParentState(state.value);
  const isConfirmationPage = parentState === ParentState.bookingConfirmation;

  const tripTotal =
    (isConfirmationPage && finalTripTotal) ||
    quoteBreakdownTripTotal ||
    finalTripTotal;

  // For confirmation page, use the actual card payment amount from fulfillment
  const cardPaymentFromFulfillment = useCheckoutStateSelector(
    getCardPaymentAmountFromFulfillment(product)
  );

  const remainingBalance = useMemo(() => {
    // On confirmation page, use the actual card payment amount from fulfillment.
    // This includes $0 when rewards fully cover the purchase (no card payment in fulfillment).
    if (isConfirmationPage && cardPaymentFromFulfillment !== undefined) {
      return cardPaymentFromFulfillment;
    }

    // Otherwise, calculate remaining balance from trip total and rewards.
    // Credits are applied via cartUpdate — the re-quoted tripTotal already
    // reflects the credit, so no frontend subtraction is needed here.
    return tripTotal
      ? getRemainingBalanceAfterRewardsApplied(
          tripTotal,
          rewardsAmountToApply,
          rewardsAmountToApplyCashEquivalent
        )
      : undefined;
  }, [
    isConfirmationPage,
    cardPaymentFromFulfillment,
    tripTotal,
    rewardsAmountToApply,
    rewardsAmountToApplyCashEquivalent,
  ]);

  return {
    remainingBalance,
    tripTotal,
    isConfirmationPage,
    rewardsAmountToApply,
    rewardsAmountToApplyCashEquivalent,
  };
};
